import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Users, Layers, ArrowLeft, Globe, X } from 'lucide-react';
import { TreeNode, deepMatch, countNodes, getChildEntries } from './TaxonomyTreeNode';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ACCENT_COLORS = [
  'from-purple-500 to-indigo-600', 'from-orange-500 to-red-600', 'from-emerald-500 to-teal-600',
  'from-blue-500 to-cyan-600', 'from-pink-500 to-rose-600', 'from-amber-500 to-yellow-600',
  'from-violet-500 to-purple-600', 'from-lime-500 to-green-600', 'from-sky-500 to-blue-600',
  'from-fuchsia-500 to-pink-600',
];

function IndustryCard({ name, val, idx, searchTerm, expertCounts }) {
  const entries = getChildEntries(val);
  const nodeCount = countNodes(val.children || {});
  const expertCount = expertCounts[name] || 0;

  return (
    <div className="bg-zinc-950 border border-zinc-800/60 rounded-2xl overflow-hidden hover:border-zinc-700/60 transition-all"
      data-testid={`industry-card-${name.replace(/[\s&,/]+/g, '-').toLowerCase()}`}>
      <div className={`bg-gradient-to-r ${ACCENT_COLORS[idx % ACCENT_COLORS.length]} p-[1px] rounded-t-2xl`}>
        <div className="bg-zinc-950 rounded-t-2xl px-5 py-4 flex items-center justify-between">
          <div>
            <h2 className="font-bold text-white text-sm">{name}</h2>
            <p className="text-[11px] text-zinc-500">{entries.length} areas · {nodeCount} nodes</p>
          </div>
          {expertCount > 0 && (
            <div className="flex items-center space-x-1 bg-emerald-500/10 px-3 py-1 rounded-full">
              <Users className="w-3 h-3 text-emerald-400" />
              <span className="text-[11px] text-emerald-400 font-semibold">{expertCount}</span>
            </div>
          )}
        </div>
      </div>
      <div className="px-3 py-3 max-h-[420px] overflow-y-auto">
        {entries.map(([cName, cVal]) => (
          <TreeNode key={cName} name={cName} node={cVal} depth={1}
            searchTerm={searchTerm} expertCounts={expertCounts} />
        ))}
      </div>
    </div>
  );
}

export default function TaxonomyExplorerPage() {
  const navigate = useNavigate();
  const [taxonomy, setTaxonomy] = useState({});
  const [stats, setStats] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [selectedIndustry, setSelectedIndustry] = useState(null);
  const searchRef = useRef(null);

  useEffect(() => {
    Promise.all([
      fetch(`${API_URL}/api/taxonomy`).then(r => r.ok ? r.json() : {}),
      fetch(`${API_URL}/api/taxonomy/stats`).then(r => r.ok ? r.json() : {}),
    ]).then(([tax, st]) => { setTaxonomy(tax); setStats(st); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const industries = useMemo(() => Object.entries(taxonomy), [taxonomy]);
  const termLower = searchTerm.toLowerCase();

  const filtered = useMemo(() => {
    let list = selectedIndustry
      ? industries.filter(([n]) => n === selectedIndustry)
      : industries;
    if (termLower) {
      list = list.filter(([n, v]) =>
        n.toLowerCase().includes(termLower) || deepMatch(v.children || v, termLower)
      );
    }
    return list;
  }, [industries, selectedIndustry, termLower]);

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="w-10 h-10 border-2 border-zinc-700 border-t-white rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white" data-testid="taxonomy-explorer-page">
      {/* Nav */}
      <nav className="sticky top-0 z-50 bg-black/90 backdrop-blur-xl border-b border-zinc-800/50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <button onClick={() => navigate('/')} className="p-2 rounded-lg hover:bg-zinc-800 transition-colors" data-testid="back-btn">
              <ArrowLeft className="w-5 h-5 text-zinc-400" />
            </button>
            <div className="flex items-center space-x-2 cursor-pointer" onClick={() => navigate('/')}>
              <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-orange-500 rounded-lg flex items-center justify-center">
                <span className="text-white font-extrabold text-xs">C</span>
              </div>
              <span className="font-bold text-lg text-white hidden sm:block">Connect</span>
            </div>
          </div>
          <button onClick={() => navigate('/signup')}
            className="bg-white text-black font-bold text-sm rounded-full px-5 py-2 hover:bg-zinc-200 transition-colors"
            data-testid="explorer-signup-btn">
            Get Started
          </button>
        </div>
      </nav>

      {/* Hero */}
      <header className="border-b border-zinc-800/50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
          <h1 className="text-2xl sm:text-4xl font-extrabold tracking-tight mb-2" data-testid="explorer-title">
            Taxonomy Explorer
          </h1>
          <p className="text-zinc-500 text-sm sm:text-base mb-6">
            The world's most comprehensive expertise tree — {(stats.total_nodes || 4880).toLocaleString()} skills across {stats.total_industries || 47} industries
          </p>

          {/* Stats */}
          <div className="flex flex-wrap gap-3 mb-8" data-testid="explorer-stats">
            {[
              { label: 'Industries', value: stats.total_industries || 47 },
              { label: 'Skills', value: (stats.total_nodes || 4880).toLocaleString() },
              { label: 'Experts', value: stats.total_experts || 0 },
              { label: 'Users', value: stats.total_users || 0 },
            ].map((s, i) => (
              <div key={i} className="flex items-center space-x-2 bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-2.5">
                <span className="text-white font-bold text-sm">{s.value}</span>
                <span className="text-zinc-500 text-xs">{s.label}</span>
              </div>
            ))}
          </div>

          {/* Search */}
          <div className="relative max-w-xl" data-testid="explorer-search">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
            <input
              ref={searchRef}
              type="text"
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); setSelectedIndustry(null); }}
              placeholder="Search any skill, subject, art, sport, language..."
              className="w-full bg-zinc-900 border border-zinc-800 rounded-xl pl-11 pr-10 py-3 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-zinc-600 focus:ring-1 focus:ring-zinc-700 transition-all"
              data-testid="explorer-search-input"
            />
            {searchTerm && (
              <button onClick={() => { setSearchTerm(''); searchRef.current?.focus(); }}
                className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-md hover:bg-zinc-800">
                <X className="w-4 h-4 text-zinc-500" />
              </button>
            )}
          </div>
          {searchTerm && (
            <p className="text-xs text-zinc-600 mt-2 ml-1" data-testid="search-result-count">
              {filtered.length} of {industries.length} industries match "<span className="text-amber-400">{searchTerm}</span>"
            </p>
          )}
        </div>
      </header>

      {/* Industry pills */}
      <div className="border-b border-zinc-800/50 overflow-hidden">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3">
          <div className="flex items-center gap-2 overflow-x-auto pb-1 scrollbar-hide">
            <button
              onClick={() => setSelectedIndustry(null)}
              className={`shrink-0 px-3 py-1.5 rounded-lg text-xs font-medium transition-all
                ${!selectedIndustry ? 'bg-white text-black' : 'bg-zinc-900 text-zinc-400 hover:text-white border border-zinc-800'}`}
              data-testid="filter-all">
              All ({industries.length})
            </button>
            {industries.map(([name]) => (
              <button key={name}
                onClick={() => setSelectedIndustry(selectedIndustry === name ? null : name)}
                className={`shrink-0 px-3 py-1.5 rounded-lg text-xs font-medium transition-all whitespace-nowrap
                  ${selectedIndustry === name ? 'bg-white text-black' : 'bg-zinc-900 text-zinc-400 hover:text-white border border-zinc-800'}`}
                data-testid={`filter-${name.replace(/[\s&,/]+/g, '-').toLowerCase()}`}>
                {name}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Cards grid */}
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
        <div className="grid gap-4 lg:grid-cols-2">
          {filtered.map(([name, val], idx) => (
            <IndustryCard key={name} name={name} val={val} idx={idx}
              searchTerm={searchTerm} expertCounts={stats.industry_counts || {}} />
          ))}
        </div>

        {filtered.length === 0 && searchTerm && (
          <div className="text-center py-20" data-testid="no-results">
            <Search className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
            <p className="text-zinc-500 text-sm">No matches for "<span className="text-white">{searchTerm}</span>"</p>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="border-t border-zinc-800/50 py-12 mt-8">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 text-center">
          <h3 className="text-xl sm:text-2xl font-bold text-white mb-2">Don't see your expertise?</h3>
          <p className="text-zinc-500 text-sm mb-6">Join and start building your unique knowledge tree</p>
          <button onClick={() => navigate('/signup')}
            className="bg-gradient-to-r from-purple-500 to-orange-500 text-white font-bold text-sm rounded-full px-8 py-3 hover:opacity-90 transition-opacity"
            data-testid="footer-signup-btn">
            Create Your Profile
          </button>
        </div>
      </footer>
    </div>
  );
}
